import { clsx } from "clsx";
import { type CSSProperties, forwardRef, type ReactElement } from "react";
import { type AriaCheckboxProps, type Placement, useCheckbox, useFocusRing, VisuallyHidden } from "react-aria";
import { useToggleState } from "react-stately";

import { useDOMRef } from "../../hooks.ts";
import { useTooltip } from "../../hooks/useTooltip.ts";
import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import styleLabel from "../Label/Label.module.css";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { AnimatedTooltip } from "../Tooltip/AnimatedTooltip.tsx";
import style from "./Switch.module.css";

export interface SwitchProps extends AriaCheckboxProps {
  title?: string;
  /** Text on the right side of the Toggle */
  label?: string | null;
  errorMessage?: string | null;
  description?: string | null;
  longDescription?: string | null;
  extraAction?: ReactElement;
  style?: CSSProperties;
  tooltip?: string;
  placement?: Placement;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(props, ref) {
  const inputRef = useDOMRef(ref);
  const divRef = useDOMRef(ref);
  const {
    label,
    errorMessage,
    description,
    longDescription,
    extraAction,
    tooltip,
    isIndeterminate,
    title,
    name,
    isRequired,
    isDisabled,
    isReadOnly,
    isInvalid,
    placement = "bottom start",
  } = props;
  const state = useToggleState(props);
  const { inputProps, labelProps } = useCheckbox(
    { ...props, "aria-label": title ?? label ?? "Toggle Switch" },
    state,
    inputRef,
  );
  const { isFocusVisible, focusProps } = useFocusRing();
  const isSelected = state.isSelected && !isIndeterminate;
  const { triggerProps, tooltipProps, state: tooltipState } = useTooltip(divRef);

  return (
    <div
      className={clsx(formStyle.input_layout, style.wrapper)}
      style={style}
      data-testid={`${name ? `${name}__` : ""}switch-wrapper`}
      {...triggerProps}
      ref={divRef}
    >
      {title != undefined && (
        <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
          <Label>{title}</Label>
          <Spacer />
          {isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
          {!isRequired && !isReadOnly && (
            <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
          )}
        </HStack>
      )}
      <Description
        data-testid="switch-description"
        className={clsx(formStyle.input_layout_column1, {
          [formStyle.description_with_tag]: title != undefined && (isReadOnly || !isRequired),
        })}
      >
        {longDescription}
      </Description>
      <label
        {...labelProps}
        className={clsx(style.group, formStyle.input_layout_column1, {
          [style.disabled]: isDisabled,
          [style.has_pointer]: !isDisabled && !isReadOnly,
        })}
      >
        <VisuallyHidden>
          <input {...inputProps} {...focusProps} ref={inputRef} />
        </VisuallyHidden>
        <div
          className={clsx(style.checkbox, {
            [style.checked]: isSelected,
            [style.focus]: isFocusVisible,
            [style.error]: isInvalid,
          })}
          aria-hidden="true"
          data-testid="switch"
        >
          {isSelected && <SymbolIcon className={style.check} icon={"check"} />}
        </div>
        {!!(label || description) && (
          <div>
            {!!label && <div className={styleLabel.label}>{label}</div>}
            {!!description && <div className={formStyle.description}>{description}</div>}
          </div>
        )}
      </label>
      {extraAction != undefined && <HStack className={formStyle.input_layout_column2}>{extraAction}</HStack>}
      {errorMessage != undefined && isInvalid && (
        <div
          role="alert"
          className={clsx(formStyle.error_message, formStyle.input_layout_column1)}
          data-testid="switch-error"
        >
          {errorMessage}
        </div>
      )}
      {tooltip && (
        <AnimatedTooltip
          targetRef={divRef}
          domProps={tooltipProps}
          title={tooltip}
          state={tooltipState}
          placement={placement}
        />
      )}
    </div>
  );
});
